import { useRouterState } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { navigationConfig } from "@/config/navigation";
import { useSidebarStore } from "@/hooks/useSidebar";
import type { NavEntry, NavLink } from "@/types/navigation";
import { isPathActive } from "./SidebarNavItem";
import { UserDropdown } from "./UserDropdown";

function flattenLinks(entries: NavEntry[]): NavLink[] {
  return entries.flatMap((entry) =>
    entry.kind === "link" ? [entry] : entry.children,
  );
}

const allLinks = flattenLinks([
  ...navigationConfig.main,
  ...(navigationConfig.secondary ?? []),
]);

/** Label of the sidebar link the current page belongs to (longest matching href). */
function useCurrentSectionLabel(): string | undefined {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return allLinks
    .filter((link) => isPathActive(pathname, link.href, link.exact))
    .sort((a, b) => b.href.length - a.href.length)[0]?.label;
}

export function Topbar() {
  const openMobile = useSidebarStore((s) => s.openMobile);
  const sectionLabel = useCurrentSectionLabel();

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-edge bg-panel px-4 lg:px-8">
      {/* Mobile: menu + logo */}
      <div className="flex items-center gap-3 lg:hidden">
        <button
          type="button"
          onClick={openMobile}
          className="cursor-pointer rounded-md p-2 text-dim transition-colors hover:bg-muted hover:text-sub"
          aria-label="Otwórz menu"
        >
          <Menu size={20} aria-hidden="true" />
        </button>
        <img src="/logos/adara-logo.svg" alt="Adara" className="h-6" />
      </div>

      {/* Desktop: where you are */}
      <p className="hidden text-[13px] text-dim lg:block">
        Adara
        {sectionLabel && (
          <>
            <span className="mx-1.5 text-faint">/</span>
            <span className="font-medium text-main">{sectionLabel}</span>
          </>
        )}
      </p>

      <div className="flex items-center gap-1">
        <UserDropdown />
      </div>
    </header>
  );
}
